import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import isString from 'lodash/isString';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { MFlex } from '../../chakra';
import MButton from '../../MButton';
import MCustomIconButton from '../../MCustomIconButton';
import MFormField from '../../MFormField';
import MInput from '../../MInput';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { DataTableFilterRef, FilterProps } from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

export const DataTableHeaderTextFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (columnKey === column.id && isString(value)) {
        setSelectedValue(value);
        if (table.options.meta?.onFilterUpdate) {
          table.options.meta.onFilterUpdate(key, value.trim() || null);
        }
      }
    },
  }));

  const key = getColumnFilterKey(column);
  const matchLabel =
    column.columnDef.meta?.textFilter?.operator === 'EQUAL'
      ? 'Equals'
      : 'Contains';

  const [selectedValue, setSelectedValue] = useState<string>(() => {
    const possibleValue = table.options.meta?.selectedFilters?.get(key);
    if (isString(possibleValue) && possibleValue) {
      return possibleValue;
    }
    return '';
  });

  const { open, onOpen, onClose } = useDisclosure();

  const handleResetFilters = () => {
    setSelectedValue('');
    table.options.meta?.selectedFilters.set(key, null);
    // Clear the TanStack column filter too. Without this a `clientControlled`
    // table keeps filtering rows after the pill and popover both say "reset".
    column.setFilterValue(null);

    // Notify server of filter update
    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, null);
    }

    onClose();
  };

  const handleApplyFilters = () => {
    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, selectedValue.trim() || null);
    }
    column.setFilterValue(selectedValue.trim() || null);
    onClose();
  };

  const handleOpen = () => {
    const possibleValue = table.options.meta?.selectedFilters?.get(key);
    let value = '';
    if (isString(possibleValue) && possibleValue) {
      value = possibleValue;
    }
    setSelectedValue(value);
  };

  const handleClose = () => {
    onClose();
    setSelectedValue('');
  };

  const filtersApplied = !!selectedValue?.trim();

  const titleId = useId();

  return (
    <Popover.Root
      initialFocusEl={() => inputRef.current}
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
          handleOpen();
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <form onSubmit={() => handleApplyFilters()}>
                <Popover.Arrow>
                  <Popover.ArrowTip />
                </Popover.Arrow>
                <Popover.CloseTrigger aria-label="Close">
                  <Icon asChild>
                    <MdClose />
                  </Icon>
                </Popover.CloseTrigger>
                <Popover.Header id={titleId}>Filter</Popover.Header>
                <Popover.Body minW="304px">
                  <MFormField label={matchLabel}>
                    <MInput
                      ref={inputRef}
                      value={selectedValue}
                      onChange={(ev) => setSelectedValue(ev.target.value)}
                      maxLength={255}
                      autoFocus
                    />
                  </MFormField>
                </Popover.Body>
                <Popover.Footer>
                  <MFlex justifyContent="space-between">
                    <MButton
                      variant="tertiary"
                      size="sm"
                      onClick={handleResetFilters}
                      isDisabled={!selectedValue}
                      type="button"
                    >
                      Reset
                    </MButton>
                    <MButton
                      size="sm"
                      onClick={handleApplyFilters}
                      type="submit"
                    >
                      Apply
                    </MButton>
                  </MFlex>
                </Popover.Footer>
              </form>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
